import { z } from "zod/v4"
import { ItemId, ItemIdSchema } from "../id"
import { DocumentSnapshotLike } from "../firestore"
import { CreatedAt, UpdatedAt } from "./system"
import { ItemInventoryDenormalized } from "./item/item"
import { SaleAppliedDenormalized } from "./item/sale"
import { ItemDistributionDenormalized } from "./item/item-distribution"

export const UserInventoryReason = z.enum(["purchase", "present", "initial-look", "subscription", "admin-grant"])
export type UserInventoryReason = z.infer<typeof UserInventoryReason>

export const UserInventoryPurchase = z.object({
  reason: z.literal("purchase"),
  item_id: ItemIdSchema.describe("アイテムID。コレクショングループクエリのために使用。"),
  item: ItemInventoryDenormalized,
  sale: SaleAppliedDenormalized.nullable(),
  distribution: ItemDistributionDenormalized,
  paid_price: z.number(),
})

export const UserInventoryPresent = z.object({
  reason: z.literal("present"),
  item_id: ItemIdSchema.describe("アイテムID。コレクショングループクエリのために使用。"),
  item: ItemInventoryDenormalized,
})

export const UserInventoryInitialLook = z.object({
  reason: z.literal("initial-look"),
  item_id: ItemIdSchema.describe("アイテムID。コレクショングループクエリのために使用。"),
  item: ItemInventoryDenormalized,
})

export const UserInventorySubscription = z.object({
  reason: z.literal("subscription"),
  item_id: ItemIdSchema.describe("アイテムID。コレクショングループクエリのために使用。"),
  item: ItemInventoryDenormalized,
})

export const UserInventoryAdminGrant = z.object({
  reason: z.literal("admin-grant"),
  item_id: ItemIdSchema.describe("アイテムID。コレクショングループクエリのために使用。"),
  item: ItemInventoryDenormalized,
})

export const UserInventoryDocumentMeta = z.discriminatedUnion("reason", [
  UserInventoryPurchase,
  UserInventoryPresent,
  UserInventoryInitialLook,
  UserInventorySubscription,
  UserInventoryAdminGrant,
])

export type UserInventoryDocumentMeta = z.infer<typeof UserInventoryDocumentMeta>

export const UserInventoryDocumentBody = z.intersection(
  UserInventoryDocumentMeta,
  z.object({
    created_at: CreatedAt,
    updated_at: UpdatedAt,
  })
)

export type UserInventoryDocumentBody = z.infer<typeof UserInventoryDocumentBody>

export type UserInventoryDocument = UserInventoryDocumentBody & {
  id: ItemId,
}

export class UserInventoryDocuments {

  static toDocumentData(doc: UserInventoryDocumentBody): object {
    return {
      ...doc,
    }
  }

  static fromDocumentSnapshot(doc: DocumentSnapshotLike | null): UserInventoryDocument | null {
    if (!doc) {
      return null
    }
    const data = doc.data()
    if (data === undefined) {
      return null
    }

    const reason = data["reason"] as UserInventoryReason
    const itemId = ItemId(doc.id)
    const base = {
      id: itemId,
      item_id: data["item_id"] ? ItemId(data["item_id"]) : itemId,
      created_at: data["created_at"],
      updated_at: data["updated_at"],
    }

    if (reason === "purchase") {
      return {
        ...base,
        reason: "purchase",
        item: data["item"],
        sale: data["sale"],
        distribution: data["distribution"],
        paid_price: data["paid_price"],
      }
    } else if (reason === "initial-look") {
      return {
        ...base,
        reason: "initial-look",
        item: data["item"],
      }
    } else if (reason === "subscription") {
      return {
        ...base,
        reason: "subscription",
        item: data["item"],
      }
    } else if (reason === "admin-grant") {
      return {
        ...base,
        reason: "admin-grant",
        item: data["item"],
      }
    } else {
      return {
        ...base,
        reason: "present",
        item: data["item"],
      }
    }
  }
}
